import React, { useState, useEffect } from 'react';
import { Button, Card, message, Row, Col, Progress, Tag, Typography, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  SoundOutlined, 
  CustomerServiceOutlined, 
  EditOutlined,
  StarOutlined, 
  SearchOutlined, 
  FileTextOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { getProgress } from '../api/learn';
import { useAuth } from '../utils/auth';
import Step1Reading from './learn/Step1Reading';
import Step2Podcast from './learn/Step2Podcast';
import Step3FillBlank from './learn/Step3FillBlank';
import Step4Score from './learn/Step4Score';
import Step5Quiz from './learn/Step5Quiz';
import Step6Promise from './learn/Step6Promise';
import './Learn.css';

const { Title, Text } = Typography;

const Learn = () => {
  const [currentStep, setCurrentStep] = useState(null); // null表示选择界面,0-5表示具体步骤
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const response = await getProgress();
      if (response.code === 200) {
        const progressData = response.data;
        setProgress(progressData);
        // 不再自动跳转，让用户手动点击完成
      }
    } catch (error) {
      message.error('加载进度失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = () => {
    loadProgress();
    setCurrentStep(null); // 返回选择界面
  };

  const handleBackToSelection = () => {
    setCurrentStep(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 处理完成提交
  const handleComplete = () => {
    navigate('/complete');
  };

  const steps = [
    { 
      id: 0,
      title: '跟读准则', 
      description: '朗读六项准则内容，准确率达到90%通过',
      icon: <SoundOutlined style={{ fontSize: 32 }} />,
      completedKey: 'step1_completed',
      color: '#1890ff'
    },
    { 
      id: 1,
      title: '听播客', 
      description: '收听准则讲解播客，加深理解',
      icon: <CustomerServiceOutlined style={{ fontSize: 32 }} />,
      completedKey: 'step2_completed',
      color: '#52c41a'
    },
    { 
      id: 2,
      title: '完形填空', 
      description: '填写准则关键词，巩固记忆',
      icon: <EditOutlined style={{ fontSize: 32 }} />,
      completedKey: 'step3_completed',
      color: '#13c2c2'
    },
    { 
      id: 3,
      title: '评分反馈', 
      description: '对六项准则进行评分',
      icon: <StarOutlined style={{ fontSize: 32 }} />,
      completedKey: 'step4_completed',
      color: '#faad14'
    },
    { 
      id: 4,
      title: '找茬游戏', 
      description: '找出场景中的违规行为',
      icon: <SearchOutlined style={{ fontSize: 32 }} />,
      completedKey: 'step5_completed',
      color: '#eb2f96'
    },
    { 
      id: 5,
      title: '自查承诺', 
      description: '选择薄弱项，填写改进承诺',
      icon: <FileTextOutlined style={{ fontSize: 32 }} />,
      completedKey: 'step6_completed',
      color: '#722ed1'
    },
  ];

  if (loading) {
    return <div className="loading">加载中...</div>;
  }

  // 计算完成进度
  const completedCount = steps.filter(s => progress[s.completedKey]).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);
  const allCompleted = completedCount === steps.length;

  // 渲染步骤选择界面
  if (currentStep === null) {
    return (
      <div className="learn-container">
        <div className="learn-header">
          <h2>学习平台</h2>
          <div className="user-info">
            <span>欢迎，{user?.name}</span>
            <Button type="link" onClick={handleLogout}>退出</Button>
          </div>
        </div>

        <Card className="progress-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>学习进度</Title>
              <Text type="secondary">请完成以下所有环节</Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              {/* 全部完成后显示完成按钮，否则显示进度环 */}
              {allCompleted ? (
                <Button
                  type="primary"
                  size="large"
                  icon={<TrophyOutlined />}
                  onClick={handleComplete}
                  style={{ 
                    minWidth: 120,
                    background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.4)'
                  }}
                >
                  完成提交
                </Button>
              ) : (
                <Progress 
                  type="circle" 
                  percent={progressPercent} 
                  format={() => `${completedCount}/${steps.length}`}
                  width={80}
                  strokeColor={{
                    '0%': '#1890ff',
                    '100%': '#52c41a',
                  }}
                />
              )}
            </div>
          </div>
          
          {/* 全部完成时显示提示 */}
          {allCompleted && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Tag color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
                🎉 恭喜！所有步骤已完成，点击"完成提交"结束学习
              </Tag>
            </div>
          )}
        </Card>

        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
          {steps.map((step) => {
            const isCompleted = progress[step.completedKey];
            
            return (
              <Col xs={24} sm={12} lg={8} key={step.id}>
                <Card
                  hoverable
                  className={`step-card ${isCompleted ? 'completed' : ''}`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  <div className="step-card-content">
                    <div className="step-icon" style={{ color: step.color }}>
                      {step.icon}
                      {isCompleted && (
                        <CheckCircleOutlined className="completed-icon" style={{ color: '#52c41a' }} />
                      )}
                    </div>
                    <Title level={4} style={{ marginTop: 16, marginBottom: 8 }}>
                      {step.title}
                    </Title>
                    <Text type="secondary">{step.description}</Text>
                    <div style={{ marginTop: 12 }}>
                      {isCompleted ? (
                        <Tag color="success">已完成</Tag>
                      ) : (
                        <Tag color="processing">待完成</Tag>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  }

  // 渲染具体步骤内容
  const step = steps[currentStep];
  
  return (
    <div className="learn-container">
      <div className="learn-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBackToSelection}
          >
            返回选择
          </Button>
          <h2 style={{ margin: 0 }}>{step.title}</h2>
        </div>
        <div className="user-info">
          <span>欢迎，{user?.name}</span>
          <Button type="link" onClick={handleLogout}>退出</Button>
        </div>
      </div>

      <Card className="learn-card">
        <div className="step-content">
          {currentStep === 0 && <Step1Reading onComplete={handleStepComplete} />}
          {currentStep === 1 && <Step2Podcast onComplete={handleStepComplete} />}
          {currentStep === 2 && <Step3FillBlank onComplete={handleStepComplete} />}
          {currentStep === 3 && <Step4Score onComplete={handleStepComplete} />}
          {currentStep === 4 && <Step5Quiz onComplete={handleStepComplete} />}
          {currentStep === 5 && <Step6Promise onComplete={handleStepComplete} />}
        </div>
      </Card>
    </div>
  );
};

export default Learn;
